import { RouteType } from "types/RouteType";
import RootStore from "../../RootStore";

interface RolePermissionRecord {
    role: string;
    routePath?: string | null;
    accessAllowed: boolean;
    location?: string | null;
}

/**
 * Manages authentication and permission checking for routes.
 * Follows Single Responsibility Principle by focusing only on permission management.
 */
export class PermissionManager {
    private rootStore: RootStore;
    private debugMode = false;
    private rolePermissionsLoaded = false;
    private rolePermissionsLoadFailed = false;
    private rolePermissionRecords: RolePermissionRecord[] = [];

    constructor(rootStore: RootStore, debugMode: boolean = false) {
        this.rootStore = rootStore;
        this.debugMode = debugMode;
    }

    /**
     * Loads role permissions from the server and stores them locally.
     * Called once per session from goTo() before permission checks.
     * Falls back to fetching via the FeathersJS service directly if the store isn't mounted yet.
     */
    async ensureRolePermissionsLoaded(): Promise<void> {
        if (this.rolePermissionsLoaded) return;
        this.rolePermissionsLoadFailed = false;

        try {
            // Try the ServiceStore first (it also populates .items for the UI)
            const rpStore = (this.rootStore as Record<string, unknown>).rolePermissionsStore as {
                items?: RolePermissionRecord[];
                find?: (query: object) => Promise<{ data?: RolePermissionRecord[] } | RolePermissionRecord[]>;
            } | undefined;

            if (rpStore?.find) {
                const response = await rpStore.find({ query: { appName: this.rootStore.appName, $limit: 5000 } });

                // Capture data directly from the response — don't rely on .entities getter
                if (response && Array.isArray(response)) {
                    this.rolePermissionRecords = response;
                } else if (response && typeof response === 'object' && 'data' in response && Array.isArray((response as { data: unknown }).data)) {
                    this.rolePermissionRecords = (response as { data: RolePermissionRecord[] }).data;
                } else if (rpStore.items && rpStore.items.length) {
                    // Fallback: read from the store's internal items array
                    this.rolePermissionRecords = rpStore.items.slice() as RolePermissionRecord[];
                }

                this.rolePermissionsLoaded = true;
                this.debugLog(`Role permissions loaded: ${this.rolePermissionRecords.length} records`);
                return;
            }

            // Fallback: if ServiceStore isn't mounted yet, try the FeathersJS service directly
            if (this.rootStore.app?.service) {
                const service = this.rootStore.app.service('role-permissions');
                if (service?.find) {
                    const response = await service.find({ query: { appName: this.rootStore.appName, $limit: 5000 } });

                    if (Array.isArray(response)) {
                        this.rolePermissionRecords = response;
                    } else if (response?.data && Array.isArray(response.data)) {
                        this.rolePermissionRecords = response.data;
                    }

                    this.rolePermissionsLoaded = true;
                    this.debugLog(`Role permissions loaded via service fallback: ${this.rolePermissionRecords.length} records`);
                }
            }
        } catch (err) {
            console.error('Error loading role permissions:', err);
        }

        // If still not loaded after all attempts, mark as failed to prevent
        // unenforced access on protected routes
        if (!this.rolePermissionsLoaded) {
            this.rolePermissionsLoadFailed = true;
            this.debugLog('Role permissions failed to load — protected routes will be denied');
        }
    }

    /**
     * Checks if the current user has permission to access a route
     * @param route - Route to check permissions for
     * @returns True if user has permission, false otherwise
     *
     * NOTE: Role/permission checks are temporarily disabled while the
     * roles/permissions system is being finalized. Only authentication
     * (signed-in status) is enforced. Re-enable the checks below once
     * user management is fully operational.
     */
    checkRoutePermission(route: RouteType): boolean {
        // Check authentication requirement (always enforced)
        if (route.requiresAuth && !this.isUserAuthenticated()) {
            this.debugLog(`Route ${route.key} requires authentication but user is not authenticated`);
            return false;
        }

        // --- Role/permission checks temporarily bypassed ---
        // TODO: Re-enable these once user management roles/permissions are fully working
        //
        // if (route.requiresAdmin && !this.isUserAdmin()) { return false; }
        // if (route.requiredRoles?.length && !this.hasRequiredRole(route.requiredRoles)) { return false; }
        // if (route.requiresPermission && !this.hasRoutePermission(route)) { return false; }
        // if (this.rolePermissionsLoadFailed && route.requiresAuth) { return false; }
        // if (!this.hasRoleRouteAccess(route)) { return false; }

        return true;
    }

    /**
     * Checks if the user is currently authenticated
     * @returns True if user is authenticated
     */
    isUserAuthenticated(): boolean {
        return this.rootStore.authStore?.isSignedIn || false;
    }

    /**
     * Checks if the user has admin privileges
     * @returns True if user is an admin
     */
    isUserAdmin(): boolean {
        const currentUser = this.rootStore.authStore?.currentUser;
        return currentUser?.adminUser || false;
    }

    /**
     * Checks if the current user has at least one of the required roles.
     * Admin role bypasses all role checks.
     */
    hasRequiredRole(requiredRoles: string[]): boolean {
        const authStore = this.rootStore.authStore;
        if (!authStore) return false;

        // Admin users bypass all role checks
        if (this.isUserAdmin()) return true;

        // Check if user has at least one of the required roles
        return requiredRoles.some(role => authStore.hasRole(role));
    }

    /**
     * Checks if the user has permission for a specific route
     * @param route - Route to check permission for
     * @returns True if user has permission
     */
    hasRoutePermission(route: RouteType): boolean {
        const currentUser = this.rootStore.authStore?.currentUser;
        if (!currentUser) {
            return false;
        }

        // If no specific permission is required, allow access
        if (!route.requiresPermission) {
            return true;
        }

        // Check user permissions against route permission path
        const userPermissions = currentUser.permissions || [];
        const routePermissionPath = route.permissionPath || route.path;

        return this.checkPermissionAccess(userPermissions, routePermissionPath || '');
    }

    /**
     * Checks if user permissions allow access to a specific path
     * @param userPermissions - Array of user permission objects
     * @param permissionPath - Path to check access for
     * @returns True if user has access
     */
    private checkPermissionAccess(userPermissions: Record<string, unknown>[], permissionPath: string): boolean {
        if (!Array.isArray(userPermissions)) {
            return false;
        }

        // Check for exact match or wildcard permissions
        return userPermissions.some(permission => {
            const permLocation = String(permission.location || permission.path || '');

            // Exact match
            if (permLocation === permissionPath) {
                return true;
            }

            // Wildcard match (e.g., "/projects/*" matches "/projects/details")
            if (permLocation.endsWith('/*')) {
                const basePath = permLocation.slice(0, -2);
                return permissionPath.startsWith(basePath);
            }

            // Parent path match (e.g., "/projects" allows "/projects/details")
            if (permissionPath.startsWith(permLocation + '/')) {
                return true;
            }

            return false;
        });
    }

    /**
     * Normalizes a route path for consistent matching (removes trailing slash).
     */
    private normalizePath(path: string): string {
        if (!path || path === '/') return path;
        return path.endsWith('/') ? path.slice(0, -1) : path;
    }

    /**
     * Checks if the user's roles grant access to a route based on DB-stored role permissions.
     * Admin bypasses all checks. Uses a two-tier default:
     *  - RESTRICTIVE for routes manageable in the permission UI (have `group`, not hidden/parent/forward):
     *    these must be explicitly granted via a role-permission record.
     *  - PERMISSIVE for routes not manageable in the UI: allowed unless explicitly denied.
     */
    hasRoleRouteAccess(route: RouteType): boolean {
        // Admin always bypasses
        if (this.isUserAdmin()) return true;

        // If no role-permission records are configured at all, the system is inactive — allow all
        if (!this.rolePermissionRecords.length) return true;

        // Routes that don't require authentication are not subject to role-permission checks
        if (!route.requiresAuth) return true;

        const routePath = route.path;
        if (!routePath) return true;

        // Never block Home/Login routes to prevent redirect loops
        const safePaths = ['/home', '/', '/login'];
        if (safePaths.includes(routePath.toLowerCase())) return true;

        // Normalize path for consistent matching
        const normalizedRoutePath = this.normalizePath(routePath);

        // Find records for this specific route (route-access records have no location)
        const routeRecords = this.rolePermissionRecords.filter((rp) => {
            const rpPath = this.normalizePath(rp.routePath || '');
            return rpPath === normalizedRoutePath && !rp.location;
        });

        // If no records exist for this route, apply restrictive or permissive default
        // based on whether the route is manageable in the permission UI.
        // Routes with a `group` appear in the permission management UI and CAN be
        // configured, so they must be explicitly granted (restrictive).
        // Routes without `group` (or hidden/parent/forward routes) can't be configured
        // in the UI, so they remain open (permissive).
        if (routeRecords.length === 0) {
            if (route.group && !route.hidden && !route.parentRoute && !route.forwardRoute) {
                this.debugLog(`Route ${routePath} denied: no role-permission records found for manageable route (restrictive default)`);
                return false;
            }
            return true;
        }

        // Check if any of the user's roles have accessAllowed (case-insensitive)
        const authStore = this.rootStore.authStore;
        if (!authStore) return false;

        const userRoles = (authStore.userRoles || []).map((r: string) => r.toLowerCase());
        this.debugLog(`Checking route ${routePath}: user roles=[${userRoles}], records=${JSON.stringify(routeRecords.map(r => ({ role: r.role, allowed: r.accessAllowed })))}`);
        return routeRecords.some(
            (rp) => rp.accessAllowed && userRoles.includes(rp.role.toLowerCase())
        );
    }

    /**
     * Gets routes that the current user has permission to access
     * @param routes - Array of routes to filter
     * @returns Array of routes the user can access
     */
    getAccessibleRoutes(routes: RouteType[]): RouteType[] {
        return routes.filter(route => this.checkRoutePermission(route));
    }

    /**
     * Handles authentication rerouting for protected routes
     * @param route - Route that requires authentication
     * @param params - Route parameters
     * @param queryParams - Query parameters
     * @returns Redirect information or null if no redirect needed
     */
    async handleAuthReroute(
        route: RouteType,
        params?: object | null,
        queryParams?: object
    ): Promise<{ route: string; params?: object; queryParams?: object } | null> {
        // If user is not authenticated and route requires auth, redirect to login
        if (route.requiresAuth && !this.isUserAuthenticated()) {
            return {
                route: 'LoginPage',
                queryParams: {
                    returnUrl: route.path,
                    ...queryParams
                }
            };
        }

        // --- Role/permission reroutes temporarily disabled ---
        // TODO: Re-enable once user management roles/permissions are fully working
        //
        // if (route.requiresAdmin && this.isUserAuthenticated() && !this.isUserAdmin()) {
        //     return { route: 'Home' };
        // }
        // if (route.requiredRoles?.length && this.isUserAuthenticated() && !this.hasRequiredRole(route.requiredRoles)) {
        //     return { route: 'Home' };
        // }
        // if (route.requiresPermission && this.isUserAuthenticated() && !this.hasRoutePermission(route)) {
        //     return { route: 'Home' };
        // }
        // if (this.rolePermissionsLoadFailed && route.requiresAuth && this.isUserAuthenticated()) {
        //     return { route: 'Home' };
        // }
        // if (this.isUserAuthenticated() && !this.hasRoleRouteAccess(route)) {
        //     return { route: 'Home' };
        // }

        return null;
    }

    /**
     * Logs permission-related messages in debug mode
     * @param message - Message to log
     * @param data - Additional data to log
     */
    private debugLog(message: string, data?: unknown): void {
        if (this.debugMode) {
            console.log(`[PermissionManager] ${message}`, data);
        }
    }

    /**
     * Gets current user information for debugging
     * @returns Object with user authentication status
     */
    getCurrentUserInfo(): {
        isAuthenticated: boolean;
        isAdmin: boolean;
        permissions: Record<string, unknown>[];
    } {
        const currentUser = this.rootStore.authStore?.currentUser;
        return {
            isAuthenticated: this.isUserAuthenticated(),
            isAdmin: this.isUserAdmin(),
            permissions: currentUser?.permissions || []
        };
    }
}
